import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Sun, Dumbbell, UtensilsCrossed, Pill, ArrowRight, CheckCircle2,
  CalendarDays, Activity,
} from "lucide-react";
import { getDiscipline } from "@/lib/trainingDisciplines";
import DisciplineBadge from "@/components/training/DisciplineBadge";

const MEAL_LABELS = {
  breakfast: "Petit-déj",
  lunch: "Déjeuner",
  dinner: "Dîner",
  snack1: "Collation 1",
  snack2: "Collation 2",
};

function exercisePreviewLabel(ex) {
  if (ex.target_weight_kg > 0) {
    const name = (ex.lift_label || ex.name)?.slice(0, 18);
    return `${name} ${ex.target_weight_kg} kg`;
  }
  if (ex.target_pace_label) return `${(ex.name || "Course").slice(0, 18)} @ ${ex.target_pace_label}`;
  if (ex.target_power_w > 0) return `${(ex.name || "Vélo").slice(0, 18)} ${ex.target_power_w} W`;
  if (ex.distance_km) return `${(ex.name || "Bloc").slice(0, 18)} ${ex.distance_km} km`;
  return (ex.name || "Exercice").slice(0, 22);
}

export default function StudentToday() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSupp, setSavingSupp] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/students/today")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleSupplement = async (itemId, checked) => {
    if (!data?.supplements) return;
    const nextChecks = { ...data.supplements.checks, [itemId]: checked };
    setData((d) => ({
      ...d,
      supplements: {
        ...d.supplements,
        checks: nextChecks,
        completed_count: Object.values(nextChecks).filter(Boolean).length,
        is_complete: d.supplements.total_count > 0
          && Object.values(nextChecks).filter(Boolean).length === d.supplements.total_count,
      },
    }));
    setSavingSupp(true);
    try {
      await api.put("/supplements/adherence/today", {
        log_date: data.date,
        checks: nextChecks,
      });
    } catch {
      toast.error("Erreur lors de la sauvegarde");
      load();
    } finally {
      setSavingSupp(false);
    }
  };

  const todayLabel = new Date().toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
  });

  if (!user?.coach_id) {
    return (
      <div className="text-center py-16">
        <Sun className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-slate-900">Session du jour</h1>
        <p className="text-slate-500 mt-2">Associez un coach pour accéder à votre programme quotidien.</p>
        <Button onClick={() => navigate("/student/dashboard")} className="mt-6 rounded-full">
          Retour au dashboard
        </Button>
      </div>
    );
  }

  const workout = data?.workout;
  const discipline = workout?.discipline || "strength";
  const meta = getDiscipline(discipline);
  const WorkoutIcon = meta.icon;

  return (
    <div data-testid="student-today">
      <div className="mb-6">
        <p className="text-sm font-medium text-amber-600 uppercase tracking-widest mb-1 flex items-center gap-1">
          <Sun className="w-3.5 h-3.5" />
          Aujourd'hui
        </p>
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
          Ma session du jour
        </h1>
        <p className="text-slate-400 mt-1 text-sm capitalize">{todayLabel}</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Entraînement */}
          <Card className={`border-l-4 ${meta.border}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.light}`}>
                    <WorkoutIcon className={`w-5 h-5 ${meta.accent}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-semibold text-slate-900">Séance — {data?.day_name}</h2>
                      <DisciplineBadge discipline={discipline} size="sm" />
                    </div>
                    {workout?.plan_title && (
                      <p className="text-xs text-slate-500 mt-0.5">{workout.plan_title}</p>
                    )}
                    {!workout?.has_session ? (
                      <p className="text-sm text-slate-500 mt-2">Jour de repos ou pas de séance prévue.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(workout.exercises || []).slice(0, 6).map((ex, i) => (
                          <Badge key={i} variant="secondary" className="text-[10px]">
                            {exercisePreviewLabel(ex)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {workout?.has_session && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 rounded-full gap-1"
                    onClick={() => navigate("/student/workout-log")}
                  >
                    Loguer <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Nutrition */}
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                    <UtensilsCrossed className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-slate-900">Repas du jour</h2>
                    {data?.diet?.plan_title && (
                      <p className="text-xs text-slate-500 mt-0.5">{data.diet.plan_title}</p>
                    )}
                    {data?.diet?.targets?.calories && (
                      <p className="text-xs text-green-700 mt-1">
                        Objectif : {Math.round(data.diet.targets.calories)} kcal
                        {data.diet.targets.protein ? ` · ${data.diet.targets.protein}g prot` : ""}
                      </p>
                    )}
                    {(data?.diet?.meals || []).length > 0 ? (
                      <ul className="mt-3 space-y-2">
                        {data.diet.meals.slice(0, 5).map((meal, i) => {
                          const logged = (data.meal_logs || []).find(
                            (m) => m.meal_type === meal.type || m.description,
                          );
                          return (
                            <li key={i} className="flex items-center gap-2 text-sm">
                              {logged?.description ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                              ) : (
                                <CalendarDays className="w-4 h-4 text-slate-300 shrink-0" />
                              )}
                              <span className="text-slate-700">{meal.name || `Repas ${i + 1}`}</span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (data?.meal_logs || []).length > 0 ? (
                      <ul className="mt-3 space-y-1">
                        {data.meal_logs.map((m) => (
                          <li key={m.meal_log_id} className="text-sm text-slate-600 flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                            {MEAL_LABELS[m.meal_type] || m.meal_type}
                            {m.calories ? ` — ${Math.round(m.calories)} kcal` : ""}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-500 mt-2">Aucun repas planifié — commencez votre journal.</p>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 rounded-full gap-1 border-green-200 text-green-700"
                  onClick={() => navigate("/student/meal-log")}
                >
                  Journal <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Compléments */}
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                  <Pill className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="font-semibold text-slate-900">Compléments</h2>
                    {data?.supplements?.total_count > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {data.supplements.completed_count}/{data.supplements.total_count}
                      </Badge>
                    )}
                  </div>
                  {(data?.supplements?.items || []).length === 0 ? (
                    <p className="text-sm text-slate-500 mt-2">Aucun complément prescrit pour aujourd'hui.</p>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {data.supplements.items.map((item) => (
                        <li key={item.item_id} className="flex items-center gap-3">
                          <Checkbox
                            checked={!!data.supplements.checks?.[item.item_id]}
                            disabled={savingSupp}
                            onCheckedChange={(v) => toggleSupplement(item.item_id, !!v)}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{item.name}</p>
                            {item.dosage && (
                              <p className="text-xs text-slate-400">{item.dosage}{item.timing ? ` · ${item.timing}` : ""}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(data?.supplements?.items || []).length > 0 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-2 px-0 text-purple-600"
                      onClick={() => navigate("/student/supplements")}
                    >
                      Voir tous mes compléments <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Raccourcis */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            {[
              { label: "Entraînement", icon: Dumbbell, path: "/student/workout-log", color: "text-orange-600 bg-orange-50" },
              { label: "Repas", icon: UtensilsCrossed, path: "/student/meal-log", color: "text-green-600 bg-green-50" },
              { label: "Compléments", icon: Pill, path: "/student/supplements", color: "text-purple-600 bg-purple-50" },
              { label: "Check-in", icon: Activity, path: "/student/dashboard", color: "text-blue-600 bg-blue-50" },
            ].map((link) => (
              <button
                key={link.path}
                type="button"
                onClick={() => navigate(link.path)}
                className={`flex flex-col items-center gap-2 p-4 rounded-2xl font-semibold text-xs transition-colors hover:opacity-90 ${link.color}`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}